import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScanSearch, RotateCcw } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Section, Empty, useBusy } from "../common";
import { fmtDateTime } from "../../lib/helpers";

export function IntegrityPanel() {
  const { t } = useLang();
  const [r, setR] = useState(null);
  const [busy, run] = useBusy();
  const check = () => run(async () => { try { setR((await api.get("/platform/integrity")).data); } catch (e) { toast.error(errMsg(e)); } });
  const bad = r ? Object.entries(r.collections).filter(([, v]) => v.no_company || v.unknown_company) : [];
  return (
    <Section title={t("Company isolation check (read-only)", "Revisión de aislamiento de empresas (solo lectura)")} testid="integrity-panel"
      action={<button className="btn-dark btn-sm" disabled={busy} onClick={check} data-testid="integrity-run-btn"><ScanSearch className="w-4 h-4" /> {t("Run check", "Revisar")}</button>}>
      {!r && <p className="p-4 text-sm text-slate-500">{t("Confirms every record belongs to exactly one company and nothing is linked across companies.", "Confirma que cada registro pertenece a una sola empresa y que nada está enlazado entre empresas.")}</p>}
      {r && (
        <div className="p-4 grid gap-2 text-sm" data-testid="integrity-result">
          <div className={`font-bold ${r.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="integrity-status">
            {r.ok ? t("All data is correctly isolated", "Todos los datos están correctamente aislados") : `${r.problems} ${t("problems found", "problemas encontrados")}`}
          </div>
          <div className="text-slate-500">{r.companies} {t("companies", "empresas")} · {Object.values(r.collections).reduce((a, v) => a + v.total, 0)} {t("records checked", "registros revisados")} · {t("cross-company links", "enlaces entre empresas")}: <b data-testid="integrity-cross">{r.cross_company_links}</b></div>
          {bad.map(([n, v]) => <div key={n} className="text-red-700 mono text-xs">{n}: {v.no_company} {t("without company", "sin empresa")}, {v.unknown_company} {t("unknown company", "empresa desconocida")}</div>)}
        </div>
      )}
    </Section>
  );
}

export function DeletedPanel() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const load = () => api.get("/platform/deleted").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const restore = async (x) => {
    if (!window.confirm(t(`Restore "${x.label}" and the records deleted with it?`, `¿Restaurar "${x.label}" y los registros borrados con él?`))) return;
    try { const { data } = await api.post(`/platform/deleted/${x.id}/restore`); toast.success(`${data.restored} ${t("restored", "restaurados")}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title={t("Deleted records archive", "Archivo de registros eliminados")} testid="deleted-panel" count={items.length}>
      {!items.length && <Empty text={t("Nothing deleted", "Nada eliminado")} testid="deleted-empty" />}
      {items.slice(0, 100).map((x) => (
        <div key={x.id} className="px-4 py-2 border-b flex items-center gap-3 text-sm" data-testid={`deleted-row-${x.id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{x.collection}: {x.label}</div>
            <div className="text-xs text-slate-500 truncate">{x.company} · {x.deleted_by} · {fmtDateTime(x.deleted_at, lang)}</div>
          </div>
          {x.restored ? <span className="text-xs font-bold text-emerald-700">{t("Restored", "Restaurado")}</span>
            : <button className="btn-outline btn-sm" onClick={() => restore(x)} data-testid={`deleted-restore-${x.id}`}><RotateCcw className="w-4 h-4" /> {t("Restore", "Restaurar")}</button>}
        </div>
      ))}
    </Section>
  );
}
